import { assertUnreachable, FeatureFlagsContext, RemoteTypeMappings, RenderDataQualityDialogParams, UIActionsContext } from '@tableau/data-catalog-shared';
import { MenuItemInfo } from '@tableau/widgets';
import { RemoteType } from 'gen-src/queries';
import React from 'react';
import { Messages } from '../../../../../gen-src/Localize';
import SvgActions from '../../../../../gen-src/icons/Actions';
import SvgActionsActive from '../../../../../gen-src/icons/ActionsActive';
import { RemoteTypeIcon } from '../datagrid/cellFormatters/RemoteTypeIcon';
import { ReRenderPlacePageContext } from '../placePage/ReRenderPlacePageContext';

export enum ColumnActions {
  Tag = 'Tag',
  Description = 'Edit Description',
  QualityWarning = 'Data Quality Warning',
  SensitiveLabels = 'Sensitivity Label'
}

export const ListMenuStyle: React.CSSProperties = {
  fontWeight: 'normal',
  top: '-1px'
};

export function ActionStatusIcon(props: { hover: boolean }): JSX.Element {
  if (props.hover) {
    return <SvgActionsActive />
  } else {
    return <SvgActions />
  }
}

export function ColumnActionItems(props: { columnLuid: string, canUpdate: boolean }): MenuItemInfo[] {
  const { customSensitivityLabelsEnabled } = React.useContext(FeatureFlagsContext);
  
  const columnActionItems: MenuItemInfo[] = [
    {
      value: ColumnActions.Tag,
      displayValue: Messages.TagAction(),
      disabled: !props.canUpdate,
    },
    {
      value: ColumnActions.Description,
      displayValue: Messages.EditDescriptionAction(),
      disabled: !props.canUpdate,
    },
    { isDivider: true },
    {
      value: ColumnActions.QualityWarning,
      displayValue: Messages.DataQualityWarningAction(),
      disabled: !props.canUpdate || !props.columnLuid,
    }
  ];

  const sensitiveLabelActionItem: MenuItemInfo[] = [{
    value: ColumnActions.SensitiveLabels,
    displayValue: Messages.SensitiveLabelAction(),
    disabled: !props.canUpdate || !props.columnLuid,
  }];

  return customSensitivityLabelsEnabled ? columnActionItems.concat(sensitiveLabelActionItem) : columnActionItems;
}

export interface ActionsMenuProps {
  readonly columnLuid: string;
  readonly columnName: string;
  readonly columnRemoteType: RemoteType;
  readonly enterDescriptionEdit?: () => void;
  readonly enterTaggingEdit?: () => void;
}

export function useActionsMenu(props: ActionsMenuProps) {
  const { columnLuid, columnName, columnRemoteType, enterDescriptionEdit, enterTaggingEdit } = props;

  const [isDialogShowing, setIsDialogShowing] = React.useState(false);
  const [action, setAction] = React.useState<ColumnActions | null>(null);
  const [isMenuShowing, setIsMenuShowing] = React.useState(false);

  const { rerender } = React.useContext(ReRenderPlacePageContext);
  const { renderDataQualityDialog, renderSensitiveLabelsDialog } = React.useContext(UIActionsContext);

  const onSelect = React.useCallback((value: string) => {

    const renderDialogParams: RenderDataQualityDialogParams = {
      content: {
        contentId: columnLuid,
        contentName: columnName,
        contentType: 'column',
        contentIcon: (<RemoteTypeIcon remoteType={columnRemoteType} />),
        contentIconTitle: RemoteTypeMappings[columnRemoteType](),
      },
      dismiss: () => {
        rerender();
      },
    };
    
    switch (value) {
      case ColumnActions.Description:
        setIsDialogShowing(true);
        setAction(value);
        enterDescriptionEdit ? enterDescriptionEdit() : null;
        break;
      case ColumnActions.Tag:
        setIsDialogShowing(true);
        setAction(value);
        enterTaggingEdit ? enterTaggingEdit() : null;
        break;
      case ColumnActions.QualityWarning:
        if (columnLuid) {
          renderDataQualityDialog(renderDialogParams).catch(_ => { return; })
        }
        break;
      case ColumnActions.SensitiveLabels:
        renderSensitiveLabelsDialog(renderDialogParams).catch(_ => { return; })
        break;
      default:
        assertUnreachable(value);
        throw new Error('Unreachable - unknown column action');  
    }
    setIsMenuShowing(false);
  },
  [columnLuid, columnName, columnRemoteType, enterDescriptionEdit, enterTaggingEdit, renderDataQualityDialog, 
    renderSensitiveLabelsDialog, rerender]
  )
  
  return { isDialogShowing, setIsDialogShowing, action, onSelect, isMenuShowing, setIsMenuShowing };
}
